import { useState, type ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import { Dropdown } from '@carbon/react';
import { AssetView } from './asset-view';
import { SlotView } from './slot-view';
import { SlotAllocationView } from './slot-allocation-view';
import { type WorkingConfigForm } from './hooks';
import { useEval3Page } from './context';
import { locatedRowAtom } from './atoms';
import { MASTER_DATA_VIEWS, masterDataViewLabel } from './utils';
import type { MasterDataView } from './types';
import styles from './master-data-panel.module.scss';

export interface MasterDataPanelProps {
  /**
   * The working-config form, owned by `Eval3NewContent` (the page root) and passed
   * down so `SlotView` gets the slot-override setters. Lifted out of this panel (T12)
   * so the slot overrides + the Eval Settings inputs are one dirty-tracked payload.
   */
  workingConfig: WorkingConfigForm;
}

/**
 * Owns the active master-data view and the view-preset dropdown, and mounts
 * exactly one view component at a time. The dropdown is constructed once and
 * passed to the active view as a slot so the view can place it in its table
 * toolbar (placement lands with the real views in later tasks). A business-heavy
 * container: reads the page identity from `Eval3PageContext` and pushes it down to
 * the active view as props. The working-config form arrives as a prop (owned by
 * `Eval3NewContent`) and is threaded to `SlotView`.
 *
 * @param {MasterDataPanelProps} props - The lifted working-config form.
 * @return {JSX.Element} The active view, with the view selector slotted in.
 */
export function MasterDataPanel({ workingConfig }: MasterDataPanelProps) {
  const [view, setView] = useState<MasterDataView>('assets');
  const dispatchLocated = useSetAtom(locatedRowAtom);
  const { packageId, calculationId } = useEval3Page();

  const viewDropdown: ReactNode = (
    <Dropdown<MasterDataView>
      id="eval3-new-master-data-view"
      className={styles.viewDropdown}
      titleText="Data view"
      hideLabel
      label="Select view"
      items={MASTER_DATA_VIEWS}
      itemToString={masterDataViewLabel}
      selectedItem={view}
      onChange={({ selectedItem }) => {
        if (selectedItem) {
          setView(selectedItem);
          // Switching view clears any pinned row (its rows are gone).
          dispatchLocated({ type: 'clear' });
        }
      }}
      size="lg"
    />
  );

  // Master-data export is no longer a per-table toolbar action — all three views export
  // through the central Content Export modal (Properties + Property Allocations carry a
  // column-preset dropdown there; Assets exports all columns). The views are now purely
  // read + (slots) bulk-edit surfaces.
  const viewProps = { packageId, calculationId, viewDropdown };

  return (
    <div className={styles.panel}>
      {view === 'assets' && <AssetView {...viewProps} />}
      {view === 'slots' && (
        <SlotView {...viewProps} workingConfig={workingConfig} />
      )}
      {view === 'allocations' && (
        <SlotAllocationView {...viewProps} workingConfig={workingConfig} />
      )}
    </div>
  );
}
